import {
  type Table,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";

import usePagination from "@/components/shared/tables/pagination";
import useTableShortcuts from "@/components/shared/tables/tableShortCuts";

import { columns } from "./columns";
import { useCostCentersDataContext } from "./cost-centers-data-provider";

const CostCentersTableInstanceContext = createContext<
  | {
      table: Table<any>;
      setRowSelection: (value: any) => void;
      onGlobalFilterChange: (value: string) => void;
    }
  | undefined
>(undefined);

export function useCostCentersTableInstanceContext() {
  const context = useContext(CostCentersTableInstanceContext);
  if (context === undefined) {
    throw new Error(
      "useCostCenterTableContext must be within CostCenterTableInstanceProvider",
    );
  }

  return context;
}

type Props = PropsWithChildren<{}>;

export const CostCenterTableInstanceProvider = ({ children }: Props) => {
  const { costCenters, updateCostCenters } = useCostCentersDataContext();
  const [filters, setFilters] = useState("");

  const [rowSelection, setRowSelection] = useState({});
  const { pagination, onPaginationChange } = usePagination();

  const _columns = useMemo(() => columns(), []);

  const table = useReactTable({
    data: costCenters ?? [],
    columns: _columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    onPaginationChange: onPaginationChange,
    onGlobalFilterChange: setFilters,
    enableMultiRowSelection: true,
    autoResetPageIndex: false,
    state: {
      rowSelection,
      pagination,
      globalFilter: filters,
    },
  });

  useTableShortcuts({
    handleCmdA: () => table.toggleAllRowsSelected(true),
    handleEsc: () => table.resetRowSelection(true),
  });

  return (
    <CostCentersTableInstanceContext.Provider
      value={{
        table,
        setRowSelection,
        onGlobalFilterChange: setFilters,
      }}
    >
      {children}
    </CostCentersTableInstanceContext.Provider>
  );
};
